import { useState, useMemo, useEffect, Fragment } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { EmptyState } from '@/components/common/EmptyState'
import { Pagination } from '@/components/common/Pagination'
import { StatusBadge } from '@/components/common/StatusBadge'
import { RefreshButton } from '@/components/common/RefreshButton'
import { SortableHeader, SortDirButton } from '@/components/common/SortableHeader'
import { TableToolbar } from '@/components/common/TableToolbar'
import { AggregationMenu } from '@/components/common/AggregationMenu'
import { TableTotalsFooter } from '@/components/common/TableTotalsFooter'
import { useColumnAggregations } from '@/hooks/useColumnAggregations'
import { COLS } from './columnasPedidosVentas'
import {
  BarraMarcas, CabeceraMarca, FilaMarcable, MarcaCelda, useMarcasFilas,
} from '@/components/common/MarcasFilas'
import { claveFila } from '@/utils/marcasFilas'
import type {
  AggColumn, FiltroEstadoCarga, PedidoVentaRow, PedidoVentaSortKey,
} from '@/types'
import { LineasRow } from './LineasRow'
import { fmtDate, fmtEur, toISO } from './formato'

const ESTADO_ABIERTO = 'Pedido abierto'

/** Pedidos que se abren solos al buscar. Ver el efecto de `expanded`. */
const MAX_AUTO_DESPLIEGUE = 10

// ── Columnas ──────────────────────────────────────────────────────────────────
// Fuente única para la cabecera y el pie de totales. El cuerpo se pinta a mano:
// cada celda tiene su propio formato (badges, avisos de vencido, acciones).


// Con Empresa.All pueden convivir pedidos en distintas divisas, así que los
// importes se desglosan por moneda en lugar de sumarse a ciegas.
const AGG_COLS: AggColumn<PedidoVentaRow>[] = [
  { key: 'importeTotal',     numeric: true, currencyKey: 'moneda', decimals: 2, defaultAgg: 'sum' },
  { key: 'importePendiente', numeric: true, currencyKey: 'moneda', decimals: 2, defaultAgg: 'sum' },
]

const AGG_KEYS = new Set(AGG_COLS.map(c => c.key))

interface Props {
  dark: boolean
  isEmpresaAll: boolean
  pedidos: PedidoVentaRow[]          // ya filtrados y ordenados
  paged: PedidoVentaRow[]
  /** Términos del buscador, combinados con OR. Bajan hasta las subtablas: si
   *  alguno casa con un artículo del pedido, sus facturas y cargas arrancan
   *  desplegadas y las filas coincidentes van resaltadas. */
  terminos?: string[]
  /** Estado de carga elegido arriba: filtra las cargas de cada pedido, no los
   *  pedidos de la lista. */
  filtroEstadoCarga?: FiltroEstadoCarga
  isLoading: boolean
  isError: boolean
  hasFilters: boolean
  page: number
  totalPages: number
  sortKey: PedidoVentaSortKey
  sortDir: 'asc' | 'desc'
  exporting: boolean
  onSort: (k: PedidoVentaSortKey) => void
  onSortDir: () => void
  onPage: (p: number) => void
  onExport: () => void
  onClearFilters: () => void
  onRetry: () => void
  onVerAdjuntos: (salesid: string) => void
  onVerAdjuntosFactura: (recid: string, factura: string) => void
  onEditar: (p: PedidoVentaRow) => void
  onCancelar: (p: PedidoVentaRow) => void
}

export function PedidosVentasTabla({
  dark, isEmpresaAll, pedidos, paged, terminos, filtroEstadoCarga, isLoading, isError, hasFilters,
  page, totalPages, sortKey, sortDir, exporting,
  onSort, onSortDir, onPage, onExport, onClearFilters, onRetry,
  onVerAdjuntos, onVerAdjuntosFactura, onEditar, onCancelar,
}: Props) {
  const { t } = useTranslation()
  const today = toISO(new Date())

  // Marcas del usuario. Un solo ámbito para toda la lista: pedidos, facturas,
  // cargas y líneas conviven en él porque la clave de cualquier sublínea empieza
  // por la de su pedido y no pueden colisionar entre sí. El almacén vive fuera de
  // React (sessionStorage), así que las marcas sobreviven a ordenar, paginar,
  // plegar el acordeón e irse al detalle y volver.
  const marcas = useMarcasFilas('pedidosVentas')

  const visibleCols = useMemo(
    () => COLS.filter(c => !c.onlyEmpresaAll || isEmpresaAll),
    [isEmpresaAll],
  )
  // `pedidos` ya viene filtrado y ordenado completo: los totales no dependen
  // de la página que se esté viendo.
  const agg = useColumnAggregations({ storageKey: 'pedidosVentas', rows: pedidos, columns: AGG_COLS })

  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // Al buscar, los pedidos que casan se abren solos para enseñar POR QUÉ han
  // salido. Con tope: cada fila desplegada dispara sus consultas de líneas y
  // cargas, así que abrir doscientos resultados serían cientos de peticiones.
  // Por encima del tope se resalta pero no se abre; el usuario despliega el que
  // le interese.
  const clave = (r: PedidoVentaRow) => `${r.salesid}-${r.dataareaid}`
  const firma = (terminos ?? []).join('|')
  const hayBusqueda = !!firma
  useEffect(() => {
    if (!firma) { setExpanded(new Set()); return }
    setExpanded(pedidos.length <= MAX_AUTO_DESPLIEGUE
      ? new Set(pedidos.map(clave))
      : new Set())
  }, [firma, pedidos])
  function toggleExpand(key: string) {
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const cardBg   = dark ? 'bg-slate-900 border-white/8' : 'bg-white border-slate-200'
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const selectCls = dark
    ? 'bg-slate-800 border-white/10 text-slate-100 cursor-pointer'
    : 'bg-white border-slate-200 text-slate-800 cursor-pointer'
  const btnBase    = 'text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer'
  const btnNeutral = dark
    ? `${btnBase} border-white/10 bg-white/5 text-slate-300 hover:bg-white/10`
    : `${btnBase} border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm`
  // Ruta + query actuales: viajan en el `state` del enlace para poder volver a
  // la lista tal y como estaba.
  const location = useLocation()
  const thBase = `px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap select-none ${mutedTxt}`
  const tdBase = `px-3 py-2.5 text-[13px] whitespace-nowrap ${dark ? 'text-slate-300' : 'text-slate-700'}`
  // `inline-flex` y no `flex`: el mismo estilo lo usan ahora botones y un
  // <Link>, y un enlace en bloque rompería la fila de acciones.
  const iconBtn = `inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg border transition-colors cursor-pointer font-medium ${
    dark
      ? 'border-white/10 bg-white/5 text-slate-400 hover:bg-sky-500/15 hover:text-sky-400 hover:border-sky-500/30'
      : 'border-slate-200 bg-white text-slate-500 hover:bg-sky-50 hover:text-sky-600 hover:border-sky-200 shadow-sm'
  }`

  return (
    <div className={`rounded-xl border overflow-hidden shadow-sm ${cardBg}`}>
      <TableToolbar
        dark={dark}
        left={
          <span className="flex items-center gap-2 flex-wrap">
            <span className={`text-[11px] font-semibold ${mutedTxt}`}>
              {pedidos.length.toLocaleString('es-ES')} {t('common.orders')}
            </span>
            {/* Por qué NO se ha abierto todo: con 9 resultados se despliegan
                solos y con 11 no, y sin decirlo parece un fallo. */}
            {hayBusqueda && pedidos.length > MAX_AUTO_DESPLIEGUE && (
              <span className={`text-[11px] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                · {t('common.filtros.resaltadas', { count: pedidos.length })}
              </span>
            )}
          </span>
        }
        right={
          <>
            <label className={`text-[10px] ${mutedTxt}`}>{t('salesOrders.sort_label')}</label>
            <select value={sortKey} onChange={e => onSort(e.target.value as PedidoVentaSortKey)}
              className={`text-[11px] px-2 py-1 rounded-lg border outline-none ${selectCls}`}>
              <option value="fechaEntrega">{t('salesOrders.sort.deliveryDate')}</option>
              <option value="fechaPedido">{t('salesOrders.sort.orderDate')}</option>
              <option value="salesid">{t('salesOrders.sort.orderId')}</option>
              <option value="nombreCliente">{t('salesOrders.sort.customer')}</option>
              <option value="customerRef">{t('salesOrders.sort.customerRef')}</option>
              <option value="salesstatus">{t('salesOrders.sort.status')}</option>
              <option value="qtyPendiente">{t('salesOrders.sort.qtyPending')}</option>
              <option value="importeTotal">{t('salesOrders.sort.totalAmount')}</option>
              <option value="importePendiente">{t('salesOrders.sort.pendingAmount')}</option>
            </select>
            <SortDirButton dark={dark} dir={sortDir} onClick={onSortDir} />
            <button
              onClick={onExport}
              disabled={pedidos.length === 0 || exporting}
              className={`${btnNeutral} disabled:opacity-40 disabled:cursor-not-allowed`}
              title={t('common.exportExcel')}
            >
              {exporting ? (
                <svg className="w-3.5 h-3.5 inline mr-1 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"/>
                  <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5 inline mr-1" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12h10M8 3v7M5 7l3 3 3-3"/>
                </svg>
              )}
              {exporting ? t('common.exporting') : t('common.exportExcel')}
            </button>
          </>
        }
      />

      {/* Cuántas filas hay marcadas y cómo quitarlas. Franja aparte y sólo
          cuando hay algo que contar: metida en la barra de herramientas
          engordaría de forma permanente un estado que casi siempre está vacío. */}
      <BarraMarcas dark={dark} marcas={marcas} />

      {isLoading && (
        <div className="px-4 py-3 space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className={`h-10 rounded-lg animate-pulse ${dark ? 'bg-white/4' : 'bg-slate-100'}`}
              style={{ opacity: 1 - i * 0.09 }} />
          ))}
        </div>
      )}

      {!isLoading && isError && (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <svg className="w-6 h-6 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
          </svg>
          <p className={`text-[12px] font-semibold ${dark ? 'text-red-400' : 'text-red-500'}`}>{t('common.error')}</p>
          <RefreshButton dark={dark} onClick={onRetry} />
        </div>
      )}

      {!isLoading && !isError && paged.length === 0 && (
        <EmptyState
          dark={dark}
          title={t('salesOrders.noResultsTitle')}
          description={t('salesOrders.noResultsSub')}
          action={hasFilters ? (
            <button onClick={onClearFilters} className={btnNeutral}>
              <svg className="w-3 h-3 text-red-400 inline mr-1" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M1 1l10 10M11 1L1 11"/>
              </svg>
              {t('common.clearFilters')}
            </button>
          ) : undefined}
        />
      )}

      {!isLoading && paged.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead className={dark ? 'bg-slate-800/50' : 'bg-slate-50/80'}>
              <tr>
                {visibleCols.map(c => {
                  if (c.key === 'marca') return <CabeceraMarca key="marca" />
                  const label = c.labelKey ? t(c.labelKey) : ''
                  const menu = AGG_KEYS.has(c.key) ? (
                    <AggregationMenu
                      dark={dark}
                      colLabel={label}
                      active={agg.aggState[c.key] ?? null}
                      onChange={fn => agg.setAgg(c.key, fn)}
                    />
                  ) : null

                  if (c.sortKey) {
                    return (
                      <SortableHeader
                        key={c.key}
                        dark={dark}
                        label={label}
                        sortKey={c.sortKey}
                        activeSortKey={sortKey}
                        activeSortDir={sortDir}
                        onClick={() => onSort(c.sortKey as PedidoVentaSortKey)}
                        align={c.align === 'right' ? 'right' : 'left'}
                        extra={menu}
                      />
                    )
                  }
                  return (
                    <th
                      key={c.key}
                      className={`group ${thBase} ${c.key === 'expand' ? 'w-8' : ''} ${c.align === 'right' ? 'text-right' : ''}`}
                    >
                      <span className={`inline-flex items-center gap-1 ${c.align === 'right' ? 'flex-row-reverse' : ''}`}>
                        {label}
                        {menu}
                      </span>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {paged.map((r, idx) => {
                const rowKey = `${r.salesid}-${r.dataareaid}`
                const isOpen = expanded.has(rowKey)
                const isVencido = !r.pendienteSync && !!r.fechaEntrega
                  && r.fechaEntrega < today && r.qtyPendiente > 0
                const editable = r.salesstatus === ESTADO_ABIERTO
                // Clave de negocio del pedido: empresa + número. El mismo nº de
                // pedido puede existir en dos entidades legales, así que el
                // número solo no identifica la fila. Nunca el índice: la lista
                // ordena y pagina, y con el índice las marcas se moverían de fila.
                const clave = claveFila(r.dataareaid, r.salesid)

                return (
                  <Fragment key={`${rowKey}-${idx}`}>
                    <FilaMarcable
                      marcas={marcas}
                      clave={clave}
                      dark={dark}
                      className={`border-t transition-colors ${
                        dark ? 'border-white/4 hover:bg-white/3' : 'border-slate-100 hover:bg-slate-50/60'
                      } ${isOpen ? (dark ? 'bg-white/4' : 'bg-slate-50') : ''}`}
                    >
                      <MarcaCelda marcas={marcas} clave={clave} dark={dark} />
                      {/* Expand */}
                      <td className={`${tdBase} pl-3 pr-1 w-8`}>
                        <button
                          onClick={() => toggleExpand(rowKey)}
                          title={isOpen ? t('salesOrders.btn.collapseLines') : t('salesOrders.btn.viewLines')}
                          className={`p-1 rounded transition-colors cursor-pointer ${
                            dark ? 'text-slate-500 hover:text-slate-200' : 'text-slate-400 hover:text-slate-700'
                          }`}
                        >
                          <svg className={`w-3.5 h-3.5 transition-transform duration-150 ${isOpen ? 'rotate-90' : ''}`}
                            viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 11l4-4-4-4"/>
                          </svg>
                        </button>
                      </td>
                      {/* Adjuntos — también en lo recién creado: el flujo de
                          Power Automate es el mismo y puede haber documentos */}
                      <td className={`${tdBase} pr-1`}>
                        <button
                          onClick={() => onVerAdjuntos(r.salesid)}
                          title={t('salesOrders.btn.viewDocs')}
                          className={iconBtn}
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 2H4a1 1 0 00-1 1v10a1 1 0 001 1h8a1 1 0 001-1V6L9 2z"/>
                            <path d="M9 2v4h4M6 9h4M6 11.5h2.5"/>
                          </svg>
                          <span>{t('salesOrders.btn.docs')}</span>
                        </button>
                      </td>
                      {/* Nº Pedido */}
                      <td className={`${tdBase} font-mono font-semibold text-[11px]`}>
                        <span className="flex items-center gap-1.5">
                          {r.salesid}
                          {r.pendienteSync && (
                            <span
                              title={t('salesOrders.sync.tooltip')}
                              className={`shrink-0 font-sans text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                                dark ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-100 text-amber-700'
                              }`}
                            >
                              {t('salesOrders.sync.badge')}
                            </span>
                          )}
                        </span>
                      </td>
                      {/* Empresa — solo Empresa.All */}
                      {isEmpresaAll && <td className={`${tdBase} text-[11px]`}>{r.dataareaid.toUpperCase()}</td>}
                      {/* Cliente */}
                      <td className={tdBase}>
                        <div className="max-w-[160px]">
                          <div className="truncate font-medium" title={r.nombreCliente ?? r.custaccount}>
                            {r.nombreCliente ?? r.custaccount}
                          </div>
                          {r.nombreCliente && (
                            <div className={`text-[10px] truncate ${mutedTxt}`}>{r.custaccount}</div>
                          )}
                        </div>
                      </td>
                      {/* Customer Ref — salestable.customerref. Vacía en las filas
                          del overlay: Dataverse expone la requisición, no este campo. */}
                      <td className={tdBase}>
                        {r.customerRef
                          ? <span className="block max-w-[140px] truncate" title={r.customerRef}>{r.customerRef}</span>
                          : <span className={mutedTxt}>—</span>}
                      </td>
                      {/* Grupo de clasificación (NAC / EXP). Vacío en las filas del
                          overlay: aún no están en Fabric. El guion dice «no lo
                          sé», no «no tiene». */}
                      <td className={tdBase}>
                        {r.grupoCliente
                          ? <span className="font-medium">{r.grupoCliente}</span>
                          : <span className={mutedTxt}>—</span>}
                      </td>
                      {/* Estado */}
                      <td className={tdBase}>
                        <StatusBadge status={r.salesstatus} dark={dark} namespace="salesOrders" />
                      </td>
                      {/* F. Pedido */}
                      <td className={`${tdBase} text-right tabular-nums`}>{fmtDate(r.fechaPedido)}</td>
                      {/* F. Entrega */}
                      <td className={`${tdBase} text-right tabular-nums`}>
                        <span className={isVencido ? (dark ? 'text-red-400 font-semibold' : 'text-red-600 font-semibold') : ''}>
                          {fmtDate(r.fechaEntrega)}
                        </span>
                        {isVencido && (
                          <div className="text-[9px] text-red-500">{t('salesOrders.overdue')}</div>
                        )}
                      </td>
                      {/* Importe total — el overlay no lo trae */}
                      <td className={`${tdBase} text-right tabular-nums`}>
                        {r.importeTotal ? fmtEur(r.importeTotal) : '—'}
                        {!r.pendienteSync && r.moneda && r.moneda !== 'EUR' && (
                          <div className={`text-[10px] ${mutedTxt}`}>{r.moneda}</div>
                        )}
                      </td>
                      {/* Importe pendiente */}
                      <td className={`${tdBase} text-right tabular-nums font-semibold`}>
                        {r.importePendiente ? (
                          <span className={dark ? 'text-amber-400' : 'text-amber-600'}>
                            {fmtEur(r.importePendiente)}
                          </span>
                        ) : '—'}
                      </td>
                      {/* Acciones: «Ver detalle» siempre; editar y cancelar sólo
                          con el pedido abierto. Es un enlace de verdad y no un
                          botón para que ctrl+clic abra el pedido aparte y se
                          puedan comparar dos. Lleva la fila en el `state`: la
                          pantalla de detalle se pinta entera desde el primer
                          frame en vez de volver a pedir la lista. */}
                      <td className={`${tdBase} text-right`}>
                        <span className="inline-flex gap-1.5">
                          <Link
                            to={`/pedidos-ventas/${encodeURIComponent(r.dataareaid)}/${encodeURIComponent(r.salesid)}`}
                            state={{ row: r, from: location.pathname + location.search }}
                            className={iconBtn}
                            title={t('salesOrders.detalle.verDetalle')}
                          >
                            <svg className="w-3 h-3" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1.5 8s2.5-4.5 6.5-4.5S14.5 8 14.5 8s-2.5 4.5-6.5 4.5S1.5 8 1.5 8z" />
                              <circle cx="8" cy="8" r="1.8" />
                            </svg>
                            {t('salesOrders.detalle.verDetalle')}
                          </Link>
                          {editable && (
                            <>
                              <button onClick={() => onEditar(r)} className={iconBtn} title={t('salesOrders.btn.edit')}>
                                <svg className="w-3 h-3" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M11.5 2.5l2 2L6 12l-2.5.5L4 10l7.5-7.5z" /><path d="M10 4l2 2" />
                                </svg>
                                {t('salesOrders.btn.edit')}
                              </button>
                              <button onClick={() => onCancelar(r)} className={iconBtn} title={t('salesOrders.btn.requestCancel')}>
                                {t('salesOrders.btn.cancel')}
                              </button>
                            </>
                          )}
                        </span>
                      </td>
                    </FilaMarcable>
                    {isOpen && (
                      <LineasRow
                        salesid={r.salesid}
                        dark={dark}
                        marcas={marcas}
                        clavePedido={clave}
                        pendienteSync={r.pendienteSync}
                        terminos={terminos}
                        filtroEstadoCarga={filtroEstadoCarga}
                        onVerAdjuntosFactura={onVerAdjuntosFactura}
                        puedeCrearCarga={r.salesstatus === ESTADO_ABIERTO}
                      />
                    )}
                  </Fragment>
                )
              })}
            </tbody>
            <TableTotalsFooter
              dark={dark}
              columns={visibleCols.map(c => ({
                key: c.key,
                decimals: AGG_COLS.find(a => a.key === c.key)?.decimals,
                align: c.align === 'right' ? 'right' : 'left',
              }))}
              results={agg.results}
              rowCount={pedidos.length}
            />
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <Pagination
          dark={dark}
          page={page}
          totalPages={totalPages}
          totalItems={pedidos.length}
          itemLabel={t('common.orders')}
          pageLabel={t('common.page')}
          ofLabel={t('common.of')}
          onFirst={() => onPage(1)}
          onPrev={() => onPage(page - 1)}
          onNext={() => onPage(page + 1)}
          onLast={() => onPage(totalPages)}
        />
      )}
    </div>
  )
}
